import React, { useCallback, useMemo } from 'react';

import { SearchableDropdownField } from 'ui-components/components/dropdown';
import { Text, TextType } from 'ui-components/components/text';

import { buildUomDropdownOptions, buildUomHierarchy, type NamedUomNode } from '../../../../../../../constants/units-of-measure';
import { emptyArray } from 'ui-components/default-props/empty';
import { purchaseOrderProductProperties } from '../../../../data/purchase-order-properties';
import styles from './uom-select-column.module.less';

type Props = {
  data: {
    unitsOfMeasure?: NamedUomNode[];
    unitOfMeasure?: number;
    productId: number;
    rowKey?: string;
    uomTypeName?: string;
  };
  handleUpdateProduct: (rowKey: string, field: string, value: number) => void;
  disabledUomIds?: readonly number[];
};

const renderSelectedValue = (option: { label?: string }) => (
  <Text element='span' textType={TextType.Paragraph} value={option.label ?? ''} />
);

export const UomSelectColumn = ({ data, handleUpdateProduct, disabledUomIds = emptyArray }: Props) => {
  const { unitsOfMeasure, unitOfMeasure, productId, rowKey } = data;

  const options = useMemo(() => {
    if (!unitsOfMeasure?.length) return [];

    return buildUomDropdownOptions(unitsOfMeasure).map((o) => ({
      ...o,
      disabled: o.id !== 0 && o.id !== unitOfMeasure && (disabledUomIds as number[]).includes(o.id),
    }));
  }, [unitsOfMeasure, unitOfMeasure, disabledUomIds]);

  const hierarchyItems = useMemo(
    () => options.map((o) => ({ id: o.id, label: o.label, eaches: o.eaches, parentId: o.parentId })),
    [options],
  );

  const renderOption = useCallback(
    (option: { id?: number; label?: string; eaches?: number; parentId?: number | null }) => {
      const label = buildUomHierarchy(
        { id: option.id ?? 0, label: option.label ?? '', eaches: option.eaches ?? 1, parentId: option.parentId ?? null },
        hierarchyItems,
      );
      return <Text className={styles['option-label']} data-uom-option element='span' textType={TextType.Paragraph} value={label} />;
    },
    [hierarchyItems],
  );

  if (!unitsOfMeasure?.length) {
    return null;
  }

  const handleSelect = (value: string) => {
    handleUpdateProduct(rowKey ?? `${productId}`, purchaseOrderProductProperties.unitOfMeasure, parseInt(value, 10));
  };

  return (
    <SearchableDropdownField
      id={`uom-select-${rowKey ?? productId}`}
      isMenuFullWidth
      onItemSelected={handleSelect}
      options={options}
      renderOption={renderOption}
      renderSelectedValue={renderSelectedValue}
      required
      selectedItemValue={unitOfMeasure != null ? String(unitOfMeasure) : '0'}
    />
  );
};
